import { Box, useTheme } from "@mui/material";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import Markdown from "react-markdown";

import PMBaseCard, { PMBaseCardProps } from "../pm-base-card/pm-base-card";

export interface PMDescriptionCardProps extends Omit<
  PMBaseCardProps,
  "children" | "fixedHeight"
> {
  /** (string) The paragraph text to render in the card body. */
  text: string;
  /** (boolean) Whether to use body2 typography instead of body1. Default: false */
  useBody2?: boolean;
  /** (string | number) Maximum height of the card. If content exceeds this, a scrollbar appears. If not provided, the card expands to fit content (original behavior). */
  maxHeight?: string | number;
}

/**
 * PMDescriptionCard
 * A simple description card using PMBaseCard.
 * Renders a title + optional icons in the header, and a block of text in the body.
 */
export const PMDescriptionCard: PimoReactComponent<PMDescriptionCardProps> = ({
  text,
  icon = "ArticleOutlined",
  title = "Description",
  showInfoIcon = false,
  showMenuIcon = false,
  useBody2 = false,
  maxHeight,
  ...rest
}) => {
  const theme = useTheme();

  return (
    <PMBaseCard
      icon={icon}
      title={title}
      showInfoIcon={showInfoIcon}
      showMenuIcon={showMenuIcon}
      fixedHeight={maxHeight ?? "auto"}
      overflow={maxHeight ? "auto" : "hidden"}
      {...rest}
    >
      <Box
        sx={{
          paddingTop: 0,
          marginTop: -2,
          fontSize: "14px",
          "& p, & ul, & ol, & li": {
            fontSize: "14px",
            lineHeight: 1.6,
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            marginTop: 0,
            marginBottom: theme.spacing(1.5),
          },
          "& br": {
            display: "block",
            marginTop: 0.5,
            content: '""',
          },
        }}
      >
        <Markdown
          components={{
            p: ({ children }) => (
              <Box
                component="p"
                sx={{
                  ...(useBody2
                    ? theme.typography.body2
                    : theme.typography.body1),
                  fontSize: "14px",
                  m: 0,
                  mt: 0,
                  mb: 1.5,
                  lineHeight: 1.6,
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {children}
              </Box>
            ),
            ul: ({ children }) => (
              <Box component="ul" sx={{ m: 0, pl: 2, mb: 1.5 }}>
                {children}
              </Box>
            ),
            ol: ({ children }) => (
              <Box component="ol" sx={{ m: 0, pl: 2, mb: 1.5 }}>
                {children}
              </Box>
            ),
            li: ({ children }) => (
              <Box component="li" sx={{ m: 0 }}>
                {children}
              </Box>
            ),
          }}
        >
          {text ?? "No Data"}
        </Markdown>
      </Box>
    </PMBaseCard>
  );
};

export default PMDescriptionCard;
